const asArray = (value, keys = []) => {
  if (Array.isArray(value)) {
    return value;
  }

  for (const key of keys) {
    if (Array.isArray(value?.[key])) {
      return value[key];
    }
  }

  return [];
};

const firstString = (...values) => values
  .map((value) => String(value || '').trim())
  .find(Boolean) || '';

const extractSourceObject = (payload = {}) => payload.source
  || payload.panopticSource
  || payload.panoptic_source
  || payload.node
  || payload.installation
  || payload.link
  || {};

const getSourceHost = (payload = {}) => {
  const source = extractSourceObject(payload);
  return firstString(
    source.host,
    source.hostname,
    source.lanHost,
    source.lan_host,
    source.address,
    source.ip,
    payload.sourceHost,
    payload.source_host,
    payload.nodeHost,
    payload.node_host,
    payload.lanHost,
    payload.lan_host,
  );
};

const getSourceName = (payload = {}) => {
  const source = extractSourceObject(payload);
  return firstString(
    source.label,
    source.name,
    source.displayName,
    source.display_name,
    payload.sourceLabel,
    payload.source_label,
    payload.sourceName,
    payload.source_name,
    payload.nodeLabel,
    payload.node_label,
    'PanOptic',
  );
};

const hasStalePayload = (...payloads) => payloads.some((payload) => Boolean(
  payload?.stale
  || payload?.cached
  || payload?.cache_stale
  || payload?.source === 'cache'
  || payload?.source === 'stale-cache',
));

const getFeedCount = (payload = {}) => asArray(payload, ['feeds', 'items', 'results']).length;

const buildPanopticSourceView = (feedsPayload = null) => {
  const name = getSourceName(feedsPayload || {});
  const host = getSourceHost(feedsPayload || {});
  const id = firstString(
    extractSourceObject(feedsPayload || {}).id,
    extractSourceObject(feedsPayload || {}).uuid,
    host,
    name,
    'panoptic-source',
  );

  return {
    id,
    name,
    host,
    label: host ? `${name} (${host})` : name,
  };
};

const getPanopticConnectionState = ({
  feedsPayload = null,
  activeWatchRules = null,
  feedsError = null,
  degraded = false,
} = {}) => {
  const status = feedsError?.response?.status;
  if (status === 403) {
    return {
      linkState: 'restricted',
      linkDetail: 'Authorization refresh is required before PanOptic data can be shown.',
    };
  }
  if (status === 404) {
    return {
      linkState: 'offline',
      linkDetail: 'No active PanOptic node link',
    };
  }
  if (feedsError) {
    return {
      linkState: 'degraded',
      linkDetail: 'PanOptic link telemetry is degraded',
    };
  }
  if (hasStalePayload(feedsPayload, activeWatchRules)) {
    return {
      linkState: 'stale',
      linkDetail: 'PanOptic data is connected but stale; refresh source state to update.',
    };
  }
  if (feedsPayload) {
    const feedCount = getFeedCount(feedsPayload);
    return {
      linkState: feedCount > 0 && !degraded ? 'connected' : 'degraded',
      linkDetail: feedCount > 0 ? `${feedCount} linked feed${feedCount === 1 ? '' : 's'}` : 'Linked node returned no feeds',
    };
  }
  return {
    linkState: degraded ? 'degraded' : 'unknown',
    linkDetail: degraded ? 'PanOptic telemetry is degraded' : 'Checking PanOptic source',
  };
};

export {
  buildPanopticSourceView,
  getPanopticConnectionState,
};
